import React, { JSX } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getFeatureFlagsFromUser,
  isFeatureFlagEnabled,
  type FeatureFlagKey,
} from "../utils/featureFlags";

// Define team role types (matching backend values)
type TeamRole = "Admin" | "Owner" | "Creator";

interface ProtectedRouteProps {
  children: JSX.Element;
  requireApproval?: boolean;
  requiredTeamRoles?: TeamRole[]; // Required team member roles to access this route
  requiredFeatureKey?: FeatureFlagKey;
  requiredSchoolTypes?: string[];
  /** Where to go when `requiredFeatureKey` is off. Defaults to home. */
  featureOffRedirectTo?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireApproval = false,
  requiredTeamRoles = [],
  requiredFeatureKey,
  requiredSchoolTypes,
  featureOffRedirectTo = "/",
}) => {
  const { token, user } = useAuth();

  // If no token is present, redirect the user to the login page.
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // If approval is required and user is not approved (team members are auto-approved)
  if (requireApproval && user?.userType !== "team" && !user?.isSchoolApproved) {
    return <Navigate to="/" replace />;
  }

  // Check role-based access for team members
  if (user?.userType === "team" && requiredTeamRoles.length > 0) {
    const hasRequiredRole = requiredTeamRoles.includes(
      user.accessLevel as TeamRole
    );
    if (!hasRequiredRole) {
      return <Navigate to="/" replace />; // Redirect to home if no access
    }
  }

  if (
    requiredFeatureKey &&
    !isFeatureFlagEnabled(getFeatureFlagsFromUser(user), requiredFeatureKey)
  ) {
    return <Navigate to={featureOffRedirectTo} replace />;
  }

  if (requiredSchoolTypes && requiredSchoolTypes.length > 0) {
    const currentSchoolType =
      typeof user?.schoolType === "string" ? user.schoolType.toLowerCase().trim() : "";
    const allowed = requiredSchoolTypes.map((t) => t.toLowerCase().trim());
    if (!allowed.includes(currentSchoolType)) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
